import React, { memo, useCallback, useState, useMemo } from 'react'
import { Typography } from '@mui/material'
import tw from 'twin.macro'
import { colors } from 'src/theme'
import useCheckPermissions from 'src/hooks/useCheckPermissions'
import { UserPermission } from 'src/constants/user'
import { FileWithPreview } from './FileWithPreview'
import SensitiveFilePlaceholder from 'src/assets/images/sensitive-file-placeholder.png'

interface Props {
  title?: string
  className?: any
  onFetchSensitiveFile?: () => string
  fileExtension?: string
  fileName?: string
}

const PDF_FILE_TYPE = 'pdf'

const SensitiveFileRowComponent = ({
  title,
  className,
  onFetchSensitiveFile,
  fileExtension,
  fileName,
}: Props) => {
  const [fetchedFileUrl, setFetchedFileUrl] = useState<string | null>(null)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const { hasPermission } = useCheckPermissions()

  const canViewSensitiveData = hasPermission(UserPermission.PersonallyIdentifiableInformationView)

  const handleOpenFile = useCallback(() => {
    if (!canViewSensitiveData || !onFetchSensitiveFile) {
      return
    }

    if (!fetchedFileUrl) {
      setFetchError(null)

      try {
        const sensitiveFileUrl = onFetchSensitiveFile()
        setFetchedFileUrl(sensitiveFileUrl)
      } catch (error) {
        setFetchError('Failed to load sensitive file')
        console.error('Error fetching sensitive file:', error)
      }
    }
  }, [canViewSensitiveData, onFetchSensitiveFile, fetchedFileUrl])

  const fileUrl = useMemo((): string | undefined => {
    if (fetchedFileUrl) {
      return fetchedFileUrl
    }

    if (onFetchSensitiveFile) {
      return onFetchSensitiveFile()
    }

    return undefined
  }, [fetchedFileUrl, onFetchSensitiveFile])

  if (!canViewSensitiveData) {
    return null
  }

  const isPdf = fileExtension?.toLowerCase() === PDF_FILE_TYPE

  return (
    <div
      css={[
        tw`py-[14px] border-0 border-b border-solid border-[#EDEDED] flex flex-col gap-1.5`,
        className,
      ]}
    >
      {title && (
        <Typography color={colors.SHADES_GREY_400} variant="h6">
          {title}
        </Typography>
      )}

      <FileWithPreview
        fullScreen={isPdf}
        previewContent={
          <img
            src={SensitiveFilePlaceholder}
            css={tw`block w-full h-auto min-h-[100px] object-cover`}
            alt="Sensitive file placeholder"
          />
        }
        onOpen={handleOpenFile}
        className={tw`rounded-lg`}
        filePath={fileUrl}
        fileName={fileName}
        fileExtension={fileExtension}
      />

      {fetchError && (
        <Typography css={tw`text-red-500`} variant="body2">
          {fetchError}
        </Typography>
      )}
    </div>
  )
}

export const SensitiveFileRow = memo(SensitiveFileRowComponent)
